import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SelectItem } from '@node_modules/primeng/api';
import { AppComponentBase } from '@shared/common/app-component-base';
import { RlaDto, GeneralFileInfoDto, PersonApplicationSummaryDto, LiaTypeServiceProxy, RlaApplicantPersonServiceProxy, RlaApplicantContactServiceProxy, RlaPersonServiceProxy, RlaBusinessServiceProxy, RlaInvoiceAddressServiceProxy, RlaDocumentServiceProxy, ExternalSystemEnum, CreateRlaInput, CreateRlaOutput, GetRlaOutput, SponsorshipApplicationSummaryDto, RlaStatus, ProfessionalLicenceDto, CorporateLicenceDto, ApplicationType, GeneralFileDto, GetRlaFileInput, ApplicationUploadFilesInput, ApplicationStatus, RlaApplicantPersonDto, GeneralDocumentGroupDto, GetRlaDocumentsInput, RlaDocumentsOutput, RlaApplicantContactDto, CreateOrUpdateRlaApplicantContact, RlaPersonDto, CreateOrUpdateRlaPerson, RlaBusinessDto, GetRlaInvoiceAddressOutput, RlaInvoiceAddressDto, InvoiceAddressDetailDto, RlaServiceProxy, RlaReplacementServiceProxy, RlaReplacementDto, CreateOrUpdateRlaContact, RlaContactDto, RlaContactServiceProxy, LegitimationPersonWithoutRegisterDto } from '@shared/service-proxies/service-proxies';
import { AppConsts } from '@shared/AppConsts';

@Injectable({providedIn: 'root'})
export class RlaService extends AppComponentBase {

    rla: RlaDto;
    rlaResultDocuments: GeneralFileInfoDto[];

    private rlapsForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly rlapsForPerson$ = this.rlapsForPersonSubject.asObservable();

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private liaService: LiaTypeServiceProxy,
        private rlaService: RlaServiceProxy,
        private rlaApplicantPersonServiceProxy: RlaApplicantPersonServiceProxy,
        private rlaApplicantContactServiceProxy: RlaApplicantContactServiceProxy,
        private rlaContactServiceProxy: RlaContactServiceProxy,
        private rlaPersonServiceProxy: RlaPersonServiceProxy,
        private rlaBusinessServiceProxy: RlaBusinessServiceProxy,
        private replacementService: RlaReplacementServiceProxy,
        private invoiceAddressService: RlaInvoiceAddressServiceProxy,
        private documentService: RlaDocumentServiceProxy,
    ) {
        super(injector);
    }

    loadRlasForPerson(gln?: string, ahv?: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln || ahv) {
            return this.rlaService.getRlasForPerson(gln ?? undefined, ahv ?? undefined).pipe(
                tap(rlaps => this.rlapsForPersonSubject.next(rlaps))
            );
        } else {
            this.rlapsForPersonSubject.next([]);
            return of([]);
        }
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaService.getExternalRegisterByProfessionId(professionId);
    }

    createRla(input: CreateRlaInput): Observable<CreateRlaOutput> {
        return this.rlaService.createRla(input).pipe(tap((output: CreateRlaOutput) => {
            this.rla = null;
        }));
    }

    deleteRla(): Observable<void> {
        return this.applicationService.deleteApplication();
    }

    getRla(caseId: string): Observable<GetRlaOutput> {
        return this.rlaService.getRla(caseId).pipe(tap((output: GetRlaOutput) => {
            this.rla = output.rla;
        }));
    }

    closeRla(): void {
        this.rla = null;
    }

    getMyRlas(): Observable<PersonApplicationSummaryDto[]> {
        return this.rlaService.getMyRlas();
    }

    isInRlaStatus(expectedStatus: RlaStatus): boolean {
        return this.rla.status === expectedStatus;
    }

    getPersonalLicenceForRla(): Observable<ProfessionalLicenceDto> {
        return this.rlaService.getLicenceForRla(this.caseId);
    }

    releaseRla(comment: string): Observable<void> {
        return this.rlaService.releaseRla(this.applicationService.caseId, comment);
    }

    getNextStatus(status: RlaStatus): Observable<RlaStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.rlaService.getNextStatus(this.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === RlaStatus.Release) {
                return RlaStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: RlaStatus): string {
        return this.applicationService.getNextStep(RlaService.getUrlForStep(nextStep));
    }

    getFile(step: RlaStatus, id: number): Observable<GeneralFileDto> {
        const input = new GetRlaFileInput();
        input.caseId = this.caseId;
        input.step = step;
        input.id = id;
        return this.documentService.getFile(input);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.documentService.uploadFiles(input);
    }

    deleteFile(step: RlaStatus, id: number): Observable<void> {
        return this.documentService.deleteFile(this.caseId, step, id);
    }

    get caseId(): string {
        return this.applicationService.caseId;
    }

    get status(): ApplicationStatus {
        return this.applicationService.status;
    }

    get professionId(): string {
        return this.rla?.professionId;
    }

    getApplicantName(): Observable<string> {
        return of(this.rla.application.name);
    }

    setApplicantPersonStepToReview() {
        this.applicationService.setStepToReview(RlaStatus.ApplicantPerson);
    }


    getApplicantPerson(): Observable<RlaApplicantPersonDto> {
        return this.rlaApplicantPersonServiceProxy.get(this.caseId);
    }

    performRegisterCheck(caseId = this.caseId): Observable<RlaApplicantPersonDto> {
        this.applicationService.setStepToReview(RlaStatus.ApplicantPerson);
        return this.rlaApplicantPersonServiceProxy.performRegisterCheck(caseId);
    }

    acceptRegisterCheck(): Observable<RlaStatus> {
        this.applicationService.setStepToReview(RlaStatus.ApplicantPerson);
        return this.rlaApplicantPersonServiceProxy.acceptRegisterCheck(this.caseId).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            })
        );
    }

    getApplicantContact(): Observable<RlaApplicantContactDto> {
        return this.rlaApplicantContactServiceProxy.get(this.caseId);
    }

    createOrUpdateApplicantContact(input: CreateOrUpdateRlaApplicantContact): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.ApplicantContact);
        return this.rlaApplicantContactServiceProxy.createOrUpdate(input).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            }),
            map((status: RlaStatus) => null)
        );
    }

    getContact(): Observable<RlaContactDto> {
        return this.rlaContactServiceProxy.get(this.caseId);
    }

    createOrUpdateContact(input: CreateOrUpdateRlaContact): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.Contact);
        return this.rlaContactServiceProxy.createOrUpdate(input).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            }),
            map((status: RlaStatus) => null)
        );
    }
    getPersonFiles(forceRefresh?: boolean, noRegisterPerson?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.documentService.getDocuments(new GetRlaDocumentsInput({ caseId: caseId, step: RlaStatus.Person, hasImmigratedLastFiveYearsId: null, noRegisterPerson: noRegisterPerson})).pipe(
            map((output: RlaDocumentsOutput) => output.documents)
        );
    }

    getPerson(): Observable<RlaPersonDto> {
        return this.rlaPersonServiceProxy.get(this.caseId);
    }

    createOrUpdatePerson(input: CreateOrUpdateRlaPerson): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.Person);
        return this.rlaPersonServiceProxy.createOrUpdate(input).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            }),
            map((status: RlaStatus) => null)
        );
    }


    getBusinesses(): Observable<RlaBusinessDto[]> {
        return this.rlaBusinessServiceProxy.getList(this.caseId);
    }

    createOrUpdateBusiness(caseId: string, input: RlaBusinessDto[]): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.Business);
        return this.rlaBusinessServiceProxy.createOrUpdate(caseId, input).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            }),
            map((status: RlaStatus) => null)
        );
    }

    getReplacement(): Observable<RlaReplacementDto> {
        return this.replacementService.get(this.caseId);
    }

    createOrUpdateReplacement(replacement: RlaReplacementDto): Observable<any> {
        this.applicationService.setStepToReview(RlaStatus.Replacement);
        return this.replacementService.createOrUpdate(this.caseId, replacement).pipe(tap((newStatusId: RlaStatus) => {
            this.rla.status = newStatusId;
        }));
    }

    getInvoiceAddress(): Observable<GetRlaInvoiceAddressOutput> {
        return this.invoiceAddressService.get(this.caseId);
    }

    createOrUpdateInvoiceAddress(invoiceAddress: RlaInvoiceAddressDto): Observable<any> {
        this.applicationService.setStepToReview(RlaStatus.InvoiceAddress);
        return this.invoiceAddressService.createOrUpdate(this.caseId, invoiceAddress).pipe(tap((newStatusId: RlaStatus) => {
            this.rla.status = newStatusId;
        }));
    }

    createAddressDropdownList(contact: InvoiceAddressDetailDto, employments: RlaBusinessDto[], onlyPrivateAddressAllowed: boolean): SelectItem[] {

        let addresses: SelectItem[] = [];
        addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(contact) });

        if (!onlyPrivateAddressAllowed) {
            employments?.forEach((employment) => {
                // Use employment id as item value
                addresses.push({ value: employment.id, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(employment) });
            });

            addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });
        }

        return addresses;
    }

    private getPrivateAddressDescription(contact: InvoiceAddressDetailDto) {
        let str = contact?.street;

        if (contact?.streetNr && contact.streetNr.length > 0) {
            str = str.concat(' ');
            str = str.concat(contact.streetNr);
        }

        str = str?.concat(', ');
        str = str?.concat(contact?.city);

        return str;
    }

    private getEmploymentAddressDescription(employment: RlaBusinessDto) {
        let str = employment?.name;

        if (employment.street?.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.street);

            if (employment.streetNr?.length > 0) {
                str = str.concat(' ');
                str = str.concat(employment.streetNr);
            }
        }

        if (employment.city?.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.city);
        }

        return str;
    }

    checkDocuments(): Observable<RlaStatus[]> {
        return this.rlaService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    static getUrlForStep(step: RlaStatus): string {
        switch (step) {
            case RlaStatus.ApplicantPerson:
                return 'applicant-person';
            case RlaStatus.ApplicantContact:
                return 'applicant-contact';
            case RlaStatus.Person:
                return 'person';
            case RlaStatus.Business:
                return 'business';
            case RlaStatus.Replacement:
                return 'replacement';
            case RlaStatus.Contact:
                return 'contact';
            case RlaStatus.InvoiceAddress:
                return 'invoice-address';
            case RlaStatus.Release:
                return 'release';
            case RlaStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    getLegitimationPersonWithoutGln(legitimationId: number): Observable<LegitimationPersonWithoutRegisterDto>{
        return this.rlaService.getLegitimationPersonWithoutRegister(legitimationId);
    }

}
